// src/components/billing/FAQManagement.js
import React, { useState } from "react";
import { Card, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Modal } from "../ui/Modal";
import { ConfirmationModal } from "../ui/ConfirmationModal";
import { apiService } from "../../services/api";
import toast from "react-hot-toast";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";

const emptyForm = {
  question: "",
  answer: "",
  sort_order: "0",
  active: true,
};

// FaqForm at module level to preserve identity across renders (prevents focus loss)
function FaqForm({ isEdit, formData, setFormData, loading, onSubmit, onCancel }) {
  const setField = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question *
        </label>
        <textarea
          value={formData.question}
          onChange={setField("question")}
          rows={2}
          required
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
          placeholder="e.g., How does billing work?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Answer *
        </label>
        <textarea
          value={formData.answer}
          onChange={setField("answer")}
          rows={4}
          required
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
          placeholder="Provide a clear, helpful answer..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sort Order
          </label>
          <input
            type="number"
            value={formData.sort_order}
            onChange={setField("sort_order")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="0"
          />
        </div>
        <div className="flex items-end pb-1">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, active: !prev.active }))
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.active ? "bg-slate-600" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="ml-3 text-sm text-gray-700">
              {formData.active ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {isEdit ? "Update FAQ" : "Create FAQ"}
        </Button>
      </div>
    </form>
  );
}

const FAQManagement = ({ faqs, onUpdate }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);

  const [formData, setFormData] = useState(emptyForm);

  const resetForm = () => setFormData(emptyForm);

  // Sort client-side by sort_order for display
  const sorted = [...(faqs || [])].sort((a, b) => a.sort_order - b.sort_order);

  const buildPayload = () => ({
    question: formData.question.trim(),
    answer: formData.answer.trim(),
    sort_order: parseInt(formData.sort_order) || 0,
    active: formData.active,
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.createFaq(buildPayload());
      toast.success("FAQ created successfully!");
      setShowCreateModal(false);
      resetForm();
      onUpdate?.();
    } catch {
      toast.error("Failed to create FAQ");
    }
    setLoading(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedFaq) return;
    setLoading(true);
    try {
      await apiService.updateFaq(selectedFaq.id, buildPayload());
      toast.success("FAQ updated successfully!");
      setShowEditModal(false);
      setSelectedFaq(null);
      resetForm();
      onUpdate?.();
    } catch {
      toast.error("Failed to update FAQ");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!faqToDelete) return;
    setDeleting(true);
    try {
      await apiService.deleteFaq(faqToDelete.id);
      toast.success("FAQ deleted successfully!");
      setShowDeleteModal(false);
      setFaqToDelete(null);
      onUpdate?.();
    } catch {
      toast.error("Failed to delete FAQ");
    }
    setDeleting(false);
  };

  const handleEditClick = (faq) => {
    setSelectedFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      sort_order: faq.sort_order?.toString() ?? "0",
      active: faq.active ?? true,
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setShowDeleteModal(true);
  };

  // Swap sort_order values between two adjacent items
  const handleReorder = async (index, direction) => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sorted.length) return;

    const a = sorted[index];
    const b = sorted[swapIndex];
    setReordering(true);
    try {
      await Promise.all([
        apiService.updateFaq(a.id, { sort_order: b.sort_order }),
        apiService.updateFaq(b.id, { sort_order: a.sort_order }),
      ]);
      onUpdate?.();
    } catch {
      toast.error("Failed to reorder FAQs");
    }
    setReordering(false);
  };

  const formProps = { formData, setFormData, loading };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            FAQ Management
          </h2>
          <p className="text-gray-600">
            Manage frequently asked questions shown on the pricing page
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {/* FAQ List */}
      {sorted.length > 0 ? (
        <div className="space-y-3">
          {sorted.map((faq, index) => (
            <Card key={faq.id}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-1 mt-1 shrink-0">
                    <button
                      onClick={() => handleReorder(index, "up")}
                      disabled={index === 0 || reordering}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move up"
                    >
                      <ChevronUpIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReorder(index, "down")}
                      disabled={index === sorted.length - 1 || reordering}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Move down"
                    >
                      <ChevronDownIcon className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          {faq.question}
                        </p>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {faq.answer}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={faq.active ? "green" : "gray"}>
                          {faq.active ? "Active" : "Inactive"}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditClick(faq)}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteClick(faq)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <QuestionMarkCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No FAQs</h3>
            <p className="mt-1 text-sm text-gray-500">
              Add your first frequently asked question.
            </p>
            <div className="mt-6">
              <Button onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        title="Add FAQ"
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
      >
        <FaqForm
          {...formProps}
          isEdit={false}
          onSubmit={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            resetForm();
          }}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        title="Edit FAQ"
        onClose={() => {
          setShowEditModal(false);
          setSelectedFaq(null);
          resetForm();
        }}
      >
        <FaqForm
          {...formProps}
          isEdit={true}
          onSubmit={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedFaq(null);
            resetForm();
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setFaqToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete FAQ"
        message={
          faqToDelete
            ? `Are you sure you want to delete this FAQ? This cannot be undone.\n\n"${faqToDelete.question}"`
            : "Are you sure you want to delete this FAQ?"
        }
        confirmText="Delete FAQ"
        loading={deleting}
      />
    </div>
  );
};

export default FAQManagement;
